import { Controller } from "@hotwired/stimulus";

const EXCLUDED = new Set(["authenticity_token", "_method"]);
const IMMEDIATE_TYPES = new Set(["checkbox", "radio", "select-one", "select-multiple"]);
const DEBOUNCE_MS = 800;
const FADE_MS = 2000;

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') || {}).content || "";
}

// Saves a form automatically as the user edits it, instead of requiring an explicit
// Save button. Usage: data-controller="autosave" on the <form>, a
// data-autosave-target="status" element for feedback text, and
// data-autosave-*-value attributes for i18n status text (see phlex-components.md).
export default class extends Controller {
  static targets = ["status"];
  static values = {
    savingMsg: String,
    savedMsg: String,
    failedMsg: String,
    retryMsg: String,
  };

  connect() {
    this._baseline = this._serialize();
    this._timer = null;
    this._fadeTimer = null;
    this._blocked = false;
    this._saving = false;
    this._pending = false;
    this._onInput = (event) => this._schedule(event, true);
    this._onChange = (event) => this._schedule(event, false);
    this.element.addEventListener("input", this._onInput);
    this.element.addEventListener("change", this._onChange);
  }

  // Called by a sibling controller (e.g. url-validator) to prevent saving while
  // a field on the form is in a known-invalid state.
  block() {
    this._blocked = true;
    clearTimeout(this._timer);
  }

  unblock() {
    this._blocked = false;
  }

  disconnect() {
    clearTimeout(this._timer);
    clearTimeout(this._fadeTimer);
    this.element.removeEventListener("input", this._onInput);
    this.element.removeEventListener("change", this._onChange);
  }

  retry() {
    this._save();
  }

  _schedule(event, debounce) {
    // Checkbox/radio/select elements fire both `input` and `change` on the same
    // interaction; only the `change` listener (debounce=false) should act on them,
    // otherwise every toggle saves twice.
    if (debounce && IMMEDIATE_TYPES.has(event.target.type)) return;
    if (this._blocked) return;

    clearTimeout(this._timer);
    if (debounce) {
      this._timer = setTimeout(() => this._save(), DEBOUNCE_MS);
    } else {
      this._save();
    }
  }

  async _save() {
    if (this._blocked) return;
    const snapshot = this._serialize();
    if (snapshot === this._baseline) return;

    // A save is already in flight — don't fire a second overlapping request (whose
    // response could resolve first and overwrite _baseline with a stale snapshot).
    // Instead, remember to immediately re-save once the in-flight one settles, so
    // this edit is never silently dropped.
    if (this._saving) {
      this._pending = true;
      return;
    }
    this._saving = true;

    this._setStatus("saving", this.savingMsgValue);
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: new FormData(this.element),
      });
      // A redirect (e.g. an expired session or entitlement gate) means the save never
      // actually happened server-side, even though the final followed response is 200 OK.
      if (!response.ok || response.redirected) throw new Error(`status ${response.status}`);
      this._baseline = snapshot;
      this._setStatus("saved", this.savedMsgValue);
      this._clearPasswordFields();
      this._fadeTimer = setTimeout(() => this._clearStatus(), FADE_MS);
    } catch {
      this._setStatus("failed", this.failedMsgValue);
    } finally {
      this._saving = false;
      if (this._pending) {
        this._pending = false;
        this._save();
      }
    }
  }

  _setStatus(state, message) {
    if (!this.hasStatusTarget) return;
    clearTimeout(this._fadeTimer);
    this.statusTarget.dataset.state = state;
    this.statusTarget.textContent = "";

    if (state === "failed") {
      this.statusTarget.append(`${message} `);
      const retry = document.createElement("button");
      retry.type = "button";
      retry.className = "link link-hover font-semibold";
      retry.textContent = this.retryMsgValue;
      retry.addEventListener("click", () => this.retry());
      this.statusTarget.append(retry);
    } else {
      this.statusTarget.textContent = message;
    }
  }

  _clearPasswordFields() {
    this.element.querySelectorAll('input[type="password"]').forEach((el) => {
      el.value = "";
    });
  }

  _clearStatus() {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = "";
    delete this.statusTarget.dataset.state;
  }

  _serialize() {
    const parts = [];
    for (const el of this.element.elements) {
      if (!el.name || EXCLUDED.has(el.name)) continue;
      if (el.disabled) continue;
      if ((el.type === "checkbox" || el.type === "radio") && !el.checked) continue;
      parts.push(`${encodeURIComponent(el.name)}=${encodeURIComponent(el.value)}`);
    }
    return parts.join("&");
  }
}
